import { randomRealAddress } from '@getstep/sdk/dist/store/testing'
import { ErrorMessage } from '@hookform/error-message'
import type { FunctionComponent } from 'react'
import { useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import {
    DateInput,
    validateAgeAtLeast,
    validateAgeAtMost,
    validateDateInput,
} from '@components/input/date-input'
import { TextInput } from '@components/input/text-input'
import { Legend, SubLegend } from '@contentful-components/forms/legend'
import type { FormProps } from '@contentful-components/forms/props'
import { CustomFieldId, useFields } from '@graphql/hooks/field'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { useDevMenuItem } from '@lib/hooks/useDevMenuItem'
import { DevActionGroup, DevActionName } from '@lib/store/ui/dev-menu'
import { useAuthorizedStores } from '@lib/store/useStores'
import type { KycFormValues } from '@lib/utils/kyc'
import { generateDateInput } from '@shared/util/tests'

export const EnterPersonalInfo: FunctionComponent<Props> = ({
    title,
    subtitle,
}) => {
    const {
        register,
        setValue,
        control,
        watch,
        formState: { errors, submitCount },
    } = useFormContext<KycFormValues>()

    const { kycStore } = useAuthorizedStores()
    const { firstName, lastName, dateOfBirth } = useFields({
        firstName: CustomFieldId.KycFirstname,
        lastName: CustomFieldId.KycLastname,
        dateOfBirth: CustomFieldId.KycDateOfBirth,
    })

    const {
        field,
        fieldState: { invalid },
    } = useController({
        control,
        name: 'personalInfo.dateOfBirth',
        rules: {
            required: {
                message: dateOfBirth.errorTexts.required,
                value: true,
            },
            validate: (value) => {
                if (!validateDateInput(value)) {
                    return dateOfBirth.errorTexts.validate
                }
                if (!validateAgeAtLeast(value, 'adult')) {
                    return dateOfBirth.errorTexts.min
                }
                if (!validateAgeAtMost(value, 125)) {
                    return dateOfBirth.errorTexts.max
                }
                return true
            },
        },
    })

    useDevMenuItem({
        group: DevActionGroup.PAGE,
        name: DevActionName.FILL_FORM,
        message: 'Filled in random data ⭐️',
        async onActivate() {
            const [{ faker }, dob] = await Promise.all([
                import('@faker-js/faker'),
                generateDateInput(),
            ])

            const { person, finance, location } = faker

            const options = {
                shouldDirty: true,
                shouldValidate: true,
            }
            setValue('personalInfo.firstName', person.firstName(), options)
            setValue('personalInfo.lastName', person.lastName(), options)
            setValue('personalInfo.dateOfBirth', dob, options)
            setValue('address', randomRealAddress(), options)
            setValue('address.line2', 'Apt. 10', options)
            setValue(
                'lastFourSsn',
                finance.creditCardNumber().slice(0, 4),
                options,
            )
            setValue(
                'address.zipPostalCode',
                location.zipCode('#####'),
                options,
            )
        },
    })

    const personalInfo = watch('personalInfo')

    useEffect(() => {
        kycStore.setValues({ personalInfo })
    }, [
        personalInfo.dateOfBirth,
        personalInfo.firstName,
        personalInfo.lastName,
    ])

    return (
        <>
            <Legend
                preset='primary'
                customFieldsetId={CustomFieldsetId.KycEnterPersonalInfo}
            >
                {title}
            </Legend>
            <SubLegend>{subtitle}</SubLegend>
            <TextInput
                size='medium'
                preset='material'
                validationCount={submitCount}
                label={firstName.label}
                autoComplete='given-name'
                data-testid='kyc.firstname.input'
                valid={!errors.personalInfo?.firstName}
                error={
                    <ErrorMessage
                        errors={errors}
                        name='personalInfo.firstName'
                    />
                }
                placeholder={firstName.placeholder}
                {...register('personalInfo.firstName', {
                    required: {
                        value: true,
                        message: firstName.errorTexts.required,
                    },
                    minLength: {
                        value: 2,
                        message: firstName.errorTexts.minLength,
                    },
                    validate: (value) => {
                        // verify that the name does not contain any numbers
                        if (/\d/.test(value)) {
                            return firstName.errorTexts.validate
                        }
                        return true
                    },
                })}
            />
            <TextInput
                size='medium'
                preset='material'
                data-testid='kyc.lastname.input'
                validationCount={submitCount}
                autoComplete='family-name'
                label={lastName.label}
                valid={!errors.personalInfo?.lastName}
                error={
                    <ErrorMessage
                        errors={errors}
                        name='personalInfo.lastName'
                    />
                }
                placeholder={lastName.placeholder}
                {...register('personalInfo.lastName', {
                    required: {
                        value: true,
                        message: lastName.errorTexts.required,
                    },
                    minLength: {
                        value: 2,
                        message: lastName.errorTexts.minLength,
                    },
                    validate: (value) => {
                        // verify that the name does not contain any numbers
                        if (/\d/.test(value)) {
                            return lastName.errorTexts.validate
                        }
                        return true
                    },
                })}
            />

            <DateInput
                {...field}
                size='medium'
                data-testid='kyc.dob.input'
                preset='material'
                validationCount={submitCount}
                label={dateOfBirth.label}
                valid={!invalid}
                error={
                    <ErrorMessage
                        errors={errors}
                        name='personalInfo.dateOfBirth'
                    />
                }
            />
        </>
    )
}

type Props = FormProps<KycFormValues>
